import React, { useEffect, useState } from "react";
import { Layout, Avatar, Dropdown, Button, Badge, Popover, Spin } from "antd";
import "./style.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGate } from "../Subscription/entitlements";
import {
  ArrowLeftOutlined,
  MenuOutlined,
  BellOutlined,
  UserAddOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  DownOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import CreditsIcon from "../../assets/img/coin-credits-tokens.png";
import {
  getNotificationsUnreadCount,
  getNotifications,
  markNotificationsRead,
} from "../ProjectHub/projectHub.service";
import type { ApiNotification } from "../ProjectHub/projectHub.service";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Header } = Layout;

interface IUser {
  firstName: string | null;
  role: string;
}

interface TopBarProps {
  setOpenSidebar: (open: boolean) => void;
}

const NOTIF_ICON: Record<ApiNotification["type"], React.ReactNode> = {
  assigned: <UserAddOutlined style={{ color: "#3b82f6", fontSize: 15 }} />,
  comment: <MessageOutlined style={{ color: "#6b7280", fontSize: 15 }} />,
  due_in_48h: (
    <ClockCircleOutlined style={{ color: "#f97316", fontSize: 15 }} />
  ),
  overdue: (
    <ExclamationCircleOutlined style={{ color: "#ef4444", fontSize: 15 }} />
  ),
};

const TopBar: React.FC<TopBarProps> = ({ setOpenSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const gate = useGate();
  const { t, i18n } = useTranslation("common");

  // ── Notification state
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  const isLoggedIn = !!localStorage.getItem("isAuthenticates");

  // Poll unread count every 30 seconds
  useEffect(() => {
    if (!isLoggedIn) return;
    const poll = async () => {
      try {
        const { unread } = await getNotificationsUnreadCount();
        setUnreadCount(unread);
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  const openNotifPanel = async () => {
    if (notifOpen) {
      setNotifOpen(false);
      return;
    }
    setNotifOpen(true);
    setNotifLoading(true);
    try {
      const res = await getNotifications({ limit: 30 });
      setNotifications(res.notifications);
      setUnreadCount(0);
      // mark stored ones read
      const ids = res.notifications
        .filter((n) => n.id !== null)
        .map((n) => n.id as string);
      if (ids.length > 0) markNotificationsRead(ids).catch(() => {});
    } catch {
      setNotifLoading(false);
    } finally {
      setNotifLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      await markNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotifClick = (n: ApiNotification) => {
    setNotifOpen(false);
    navigate(`/project-hub/${n.project_id}`);
  };

  const notifContent = (
    <div className="notif-panel">
      <div className="notif-panel-header">
        <span className="notif-panel-title">{t("header.notifications")}</span>
        {notifications.some((n) => n.id !== null && !n.is_read) && (
          <button className="notif-mark-all" onClick={markAllRead}>
            <CheckOutlined /> {t("header.markAllRead")}
          </button>
        )}
      </div>
      {notifLoading ? (
        <div className="notif-loading">
          <Spin size="small" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="notif-empty">{t("header.noNotifications")}</div>
      ) : (
        <div className="notif-list">
          {notifications.map((n, i) => (
            <div
              key={n.id ?? `computed-${i}`}
              className={`notif-item ${!n.is_read && n.id !== null ? "notif-item--unread" : ""}`}
              onClick={() => handleNotifClick(n)}
            >
              <div className="notif-item-icon">{NOTIF_ICON[n.type]}</div>
              <div className="notif-item-body">
                <p className="notif-item-msg">{n.message}</p>
                <span className="notif-item-time">
                  {dayjs(n.created_at).fromNow()}
                </span>
              </div>
              {!n.is_read && n.id !== null && <div className="notif-dot" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── User menu
  const onMenuClick = (event: any) => {
    if (event.key === "3") {
      const isRememberMe = localStorage.getItem("rememberMe");
      localStorage.removeItem("token");
      localStorage.removeItem("organizationId");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticates");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("currentProjectId");
      localStorage.removeItem("currentWorkspaceId");
      localStorage.removeItem("i18nextLng");
      if (!isRememberMe) localStorage.removeItem("rememberMe");
      navigate("/");
    }
    if (event.key === "0") navigate("/profile-information");
  };

  const userMenuItems = {
    items: [
      { key: "0", label: t("header.profile") },
      { type: "divider" as const },
      { key: "3", label: t("header.logout") },
    ],
    onClick: onMenuClick,
  };

  const currentLanguage = i18n.language.split("-")[0];
  const languageMenuItems = {
    items: [
      { key: "en", label: t("languages.en") },
      { key: "ar", label: t("languages.ar") },
    ],
    selectedKeys: [currentLanguage],
    onClick: (e: { key: string }) => i18n.changeLanguage(e.key),
  };

  let getUserString = localStorage.getItem("user");
  let username = "";
  let role = "role";
  if (getUserString) {
    const userObject = JSON.parse(getUserString);
    username = userObject.username;
    if (userObject.roles && userObject.roles.length > 0) {
      role = userObject.roles[0].role_name;
    }
  }
  const user: IUser = { firstName: username, role };
  const userInitial =
    user?.firstName !== null ? user?.firstName?.charAt(0) : "";

  const isProjectDetailsPage =
    location.pathname.includes("/projects/") &&
    location.pathname.includes("/details");
  const isProjectsListPage = location.pathname.startsWith("/projects");
  const isSurveyManagementPage =
    location.pathname.startsWith("/ai-survey-generator") ||
    location.pathname.startsWith("/survey-builder") ||
    location.pathname.startsWith("/upload-survey") ||
    location.pathname.startsWith("/ai-translation") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/reporting") ||
    location.pathname.startsWith("/audit-trails") ||
    location.pathname.startsWith("/project-hub") ||
    location.pathname.startsWith("/library") ||
    location.pathname.startsWith("/data-anonymisation") ||
    location.pathname.startsWith("/doc-processing") ||
    location.pathname.startsWith("/data-cleaning") ||
    location.pathname.startsWith("/help-center") ||
    location.pathname.startsWith("/balanced-scorecard") ||
    location.pathname.startsWith("/formula-builder");

  const isNewTranslationPage = location.pathname.startsWith(
    "/add-ai-translation",
  );
  const isDataCleaningReviewPage =
    location.pathname.includes("/data-cleaning/") &&
    location.pathname.includes("/review/");

  let projectId = localStorage.getItem("currentProjectId");

  const handleBackButtonClick = () => {
    if (isDataCleaningReviewPage) {
      navigate(`/data-cleaning/${projectId}`);
    } else if (isNewTranslationPage) {
      navigate(`/ai-translation/${projectId}`);
    } else if (isSurveyManagementPage) {
      if (projectId) navigate(`/projects/${projectId}/details`);
      else navigate("/workspaces");
    } else if (isProjectDetailsPage) {
      const workspaceId = localStorage.getItem("currentWorkspaceId");
      if (workspaceId) navigate(`/projects/${workspaceId}`);
      else navigate("/workspaces");
    } else {
      navigate("/workspaces");
    }
  };

  const backButtonText = isDataCleaningReviewPage
    ? t("header.backToProjectDashboard")
    : isNewTranslationPage
      ? t("header.backToTranslationList")
      : isSurveyManagementPage
        ? t("header.backToProjectDashboard")
        : isProjectDetailsPage
          ? t("header.backToProjects")
          : t("header.backToWorkspaces");

  const headerClassName = `${isProjectDetailsPage || !location.pathname.startsWith("/projects") ? "header" : "removeMargin"}`;

  return (
    <Header className={headerClassName}>
      <div className="go-back-div">
        <div className="hamburger-item" onClick={() => setOpenSidebar(true)}>
          <MenuOutlined />
        </div>
        {(isProjectDetailsPage ||
          isProjectsListPage ||
          isSurveyManagementPage ||
          isNewTranslationPage) && (
          <Button
            className="go-backButton"
            onClick={handleBackButtonClick}
            style={{ marginRight: "20px" }}
          >
            <ArrowLeftOutlined />
            {backButtonText}
          </Button>
        )}
      </div>

      <div className="header-content">
        <div className="user-info">
          {isLoggedIn && gate.tokenBalance && (
            <button
              className="topbar-token-pill"
              onClick={() => navigate("/subscription-plans")}
              title={t("header.tokenTooltip")}
            >
              <img src={CreditsIcon} alt="" className="topbar-token-icon" />
              {gate.plan?.unlimitedCredits
                ? "Unlimited"
                : `${gate.tokenBalance.balance.toLocaleString()} ${t("header.credits")}`}
            </button>
          )}
          {/* Language switcher */}
          <Dropdown menu={languageMenuItems} trigger={["click"]}>
            <button
              className="notif-bell-btn"
              title={t("language")}
              aria-label={t("language")}
            >
              <GlobalOutlined />
            </button>
          </Dropdown>
          {/* Notification bell */}
          {isLoggedIn && (
            <Popover
              open={notifOpen}
              onOpenChange={(v) => {
                if (!v) setNotifOpen(false);
              }}
              content={notifContent}
              trigger="click"
              placement="bottomRight"
              arrow={false}
              overlayClassName="notif-popover"
            >
              <Badge
                count={unreadCount}
                size="small"
                className="notif-badge-wrapper"
              >
                <button className="notif-bell-btn" onClick={openNotifPanel}>
                  <BellOutlined />
                </button>
              </Badge>
            </Popover>
          )}
          <Dropdown menu={userMenuItems} trigger={["click"]}>
            <div className="topbar-user-box">
              <Avatar
                style={{
                  backgroundColor: "#ede9fe",
                  color: "#6d28d9",
                  fontWeight: 600,
                  verticalAlign: "middle",
                }}
                size={36}
              >
                {userInitial}
              </Avatar>
              <div className="user-info-content">
                <span className="user-name">{user.firstName}</span>
                <span className="user-role">{user.role}</span>
              </div>
              <DownOutlined className="topbar-user-caret" />
            </div>
          </Dropdown>
        </div>
      </div>
    </Header>
  );
};

export default TopBar;
